import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { ArrowLeft, Edit, Plus, Printer, Save, Target, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const ICS233Form = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const [view, setView] = useState('list');
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    exercise_name: currentExercise?.exercise_name || '',
    action_items: []
  });
  const [loading, setLoading] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  // Fetch all ICS 233 entries for current exercise
  const fetchEntries = async (silent = false) => {
    if (!currentExercise?.id) return;
    
    if (!silent) setLoading(true);
    try {
      const response = await fetch(`${API}/ics-233?exercise_id=${currentExercise.id}`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
        setLastSynced(new Date());
      }
    } catch (error) {
      if (!silent) console.error('Error fetching ICS 233 entries:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [currentExercise?.id]);

  // Setup polling for real-time sync (every 30 seconds)
  useEffect(() => {
    if (currentExercise?.id) {
      const pollInterval = setInterval(() => {
        fetchEntries(true);
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [currentExercise?.id]);

  // Initialize form with editing data
  useEffect(() => {
    if (editingEntry) {
      setFormData({
        exercise_name: editingEntry.exercise_name || currentExercise?.exercise_name || '',
        action_items: editingEntry.action_items || []
      });
    } else {
      setFormData({
        exercise_name: currentExercise?.exercise_name || '',
        action_items: []
      });
    }
  }, [editingEntry, currentExercise]);

  // Add new action item
  const addActionItem = () => {
    const newItem = {
      no: formData.action_items.length + 1,
      item: '',
      for_poc: '',
      briefed_poc: false,
      start_date: '',
      status: '',
      target_date: '',
      actual_date: ''
    };
    setFormData(prev => ({
      ...prev,
      action_items: [...prev.action_items, newItem]
    }));
  };

  // Remove action item
  const removeActionItem = (index) => {
    setFormData(prev => ({
      ...prev,
      action_items: prev.action_items.filter((_, i) => i !== index).map((item, i) => ({
        ...item,
        no: i + 1 // Re-number items
      }))
    }));
  };

  // Update action item
  const updateActionItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      action_items: prev.action_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Handle save
  const handleSave = async () => {
    if (!currentExercise?.id) return;

    setLoading(true);
    try {
      const payload = {
        exercise_id: currentExercise.id,
        exercise_name: formData.exercise_name,
        action_items: formData.action_items
      };

      let response;
      if (editingEntry) {
        response = await fetch(`${API}/ics-233/${editingEntry.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API}/ics-233`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        await fetchEntries();
        setView('list');
        setEditingEntry(null);
        setFormData({
          exercise_name: currentExercise?.exercise_name || '',
          action_items: []
        });
      } else {
        console.error('Error saving ICS 233 entry');
      }
    } catch (error) {
      console.error('Error saving ICS 233 entry:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete entire entry
  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this ICS 233 entry?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/ics-233/${entryId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        await fetchEntries();
      }
    } catch (error) {
      console.error('Error deleting ICS 233 entry:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete individual action item from list view
  const handleDeleteActionItem = async (entry, actionItemIndex) => {
    if (!window.confirm('Are you sure you want to delete this action item?')) return;

    setLoading(true);
    try {
      // Create updated action items array without the deleted item
      const updatedActionItems = entry.action_items
        .filter((_, i) => i !== actionItemIndex)
        .map((item, i) => ({ ...item, no: i + 1 })); // Re-number items

      // Update the entry with the new action items
      const response = await fetch(`${API}/ics-233/${entry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          exercise_id: currentExercise.id,
          exercise_name: entry.exercise_name,
          action_items: updatedActionItems
        })
      });

      if (response.ok) {
        await fetchEntries();
      }
    } catch (error) {
      console.error('Error deleting action item:', error);
    } finally {
      setLoading(false);
    }
  };

  // Print function
  const handlePrint = (entry = null) => {
    const entryToPrint = entry || {
      exercise_name: formData.exercise_name,
      action_items: formData.action_items
    };

    const printContent = `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <div style="text-align: center; margin-bottom: 20px;">
          <h2>ICS 233 - INCIDENT OPEN ACTION TRACKER</h2>
          <h3>Exercise: ${entryToPrint.exercise_name}</h3>
        </div>
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr style="background-color: #f0f0f0;">
              <th style="border: 1px solid #000; padding: 8px; font-weight: bold;">No.</th>
              <th style="border: 1px solid #000; padding: 8px; font-weight: bold;">Item</th>
              <th style="border: 1px solid #000; padding: 8px; font-weight: bold;">For/POC</th>
              <th style="border: 1px solid #000; padding: 8px; font-weight: bold;">Briefed POC</th>
              <th style="border: 1px solid #000; padding: 8px; font-weight: bold;">Start Date</th>
              <th style="border: 1px solid #000; padding: 8px; font-weight: bold;">Status</th>
              <th style="border: 1px solid #000; padding: 8px; font-weight: bold;">Target Date</th>
              <th style="border: 1px solid #000; padding: 8px; font-weight: bold;">Actual Date</th>
            </tr>
          </thead>
          <tbody>
            ${entryToPrint.action_items?.map(item => `
              <tr>
                <td style="border: 1px solid #000; padding: 8px; text-align: center;">${item.no}</td>
                <td style="border: 1px solid #000; padding: 8px;">${item.item}</td>
                <td style="border: 1px solid #000; padding: 8px;">${item.for_poc}</td>
                <td style="border: 1px solid #000; padding: 8px; text-align: center;">${item.briefed_poc ? '✓' : ''}</td>
                <td style="border: 1px solid #000; padding: 8px;">${item.start_date}</td>
                <td style="border: 1px solid #000; padding: 8px;">${item.status}</td>
                <td style="border: 1px solid #000; padding: 8px;">${item.target_date}</td>
                <td style="border: 1px solid #000; padding: 8px;">${item.actual_date}</td>
              </tr>
            `).join('') || '<tr><td colspan="8" style="border: 1px solid #000; padding: 8px; text-align: center;">No action items added</td></tr>'}
          </tbody>
        </table>
        <div style="margin-top: 30px; text-align: center; font-size: 12px; color: #666;">
          Generated by EXRSIM - Emergency Response Simulation Platform
        </div>
      </div>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, `
      <html>
        <head>
          <title>ICS 233 - Incident Open Action Tracker</title>
          <style>
            @media print {
              body { margin: 0; }
              @page { margin: 1in; }
            }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          ${printContent}
        </body>
      </html>
    `);
  };

  // List view
  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <Target className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-4`}>
              ICS 233 - Incident Open Action Tracker
            </h2>
            <p className={`${theme.colors.textMuted} mb-6 max-w-2xl mx-auto`}>
              Track and monitor open action items, their status, and resolution progress throughout the exercise.
            </p>
          </div>

          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-lg font-semibold ${theme.colors.textPrimary}`}>
              Action Tracker Records
            </h3>
            <Button
              onClick={() => setView('add')}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First ICS 233
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
              <p className={`mt-2 ${theme.colors.textMuted}`}>Loading entries...</p>
            </div>
          ) : entries.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-8 text-center">
                <Target className={`h-12 w-12 ${theme.colors.textMuted} mx-auto mb-4`} />
                <h3 className={`text-lg font-medium ${theme.colors.textPrimary} mb-2`}>
                  No ICS 233 entries yet
                </h3>
                <p className={`${theme.colors.textMuted} mb-4`}>
                  Create your first incident open action tracker to begin monitoring action items.
                </p>
                <Button
                  onClick={() => setView('add')}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create First ICS 233
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {entries.map((entry) => (
                <Card key={entry.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-medium text-white mb-2">
                          Exercise: {entry.exercise_name}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {entry.action_items?.length || 0} action items • Created: {new Date(entry.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePrint(entry)}
                          className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingEntry(entry);
                            setView('edit');
                          }}
                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(entry.id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {entry.action_items?.length > 0 && (
                      <div className="mt-4 overflow-x-auto">
                        <div className="min-w-[850px]"> {/* Ensure minimum width for all columns */}
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-600">
                                <th className="text-left py-2 px-2 text-gray-300 min-w-[40px]">No.</th>
                                <th className="text-left py-2 px-2 text-gray-300 min-w-[150px]">Item</th>
                                <th className="text-left py-2 px-2 text-gray-300 min-w-[100px]">For/POC</th>
                                <th className="text-center py-2 px-2 text-gray-300 min-w-[60px]">Briefed</th>
                                <th className="text-left py-2 px-2 text-gray-300 min-w-[120px]">Status</th>
                                <th className="text-left py-2 px-2 text-gray-300 min-w-[100px]">Target Date</th>
                                <th className="text-left py-2 px-2 text-gray-300 min-w-[100px]">Actual Date</th>
                                <th className="text-center py-2 px-2 text-gray-300 min-w-[50px]">Delete</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entry.action_items.slice(0, 3).map((item, index) => (
                                <tr key={`ics233-${index}`} className="border-b border-gray-700">
                                  <td className="py-2 px-2 text-white">{item.no}</td>
                                  <td className="py-2 px-2 text-white truncate" title={item.item}>{item.item}</td>
                                  <td className="py-2 px-2 text-white">{item.for_poc}</td>
                                  <td className="py-2 px-2 text-center">{item.briefed_poc ? '✓' : ''}</td>
                                  <td className="py-2 px-2 text-white truncate" title={item.status}>{item.status}</td>
                                  <td className="py-2 px-2 text-white">{item.target_date}</td>
                                  <td className="py-2 px-2 text-white">{item.actual_date}</td>
                                  <td className="py-2 px-2 text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteActionItem(entry, index)}
                                      className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                      title="Delete this action item"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                              {entry.action_items.length > 3 && (
                                <tr>
                                  <td colSpan="8" className="py-2 px-2 text-gray-400 text-center">
                                    ... and {entry.action_items.length - 3} more items (click Edit to manage all)
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Form view (add/edit)
  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => {
                setView('list');
                setEditingEntry(null);
              }}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to List
            </Button>
            <h2 className={`text-2xl font-bold ${theme.colors.textPrimary}`}>
              {editingEntry ? 'Edit' : 'Create'} ICS 233 - Incident Open Action Tracker
            </h2>
          </div>
          <Button
            onClick={() => handlePrint()}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print ICS 233
          </Button>
        </div>

        {/* Exercise Name Display */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-orange-500">Exercise Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-white text-lg font-medium">
              Exercise Name: {formData.exercise_name}
            </div>
          </CardContent>
        </Card>

        {/* Action Items Table */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader className="pb-4">
            <div className="flex justify-between items-center">
              <CardTitle className="text-orange-500">Action Items</CardTitle>
              <Button
                onClick={addActionItem}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Action Item
              </Button>
            </div>
            {formData.action_items.length > 0 && (
              <div className="text-sm text-gray-400 mt-2">
                💡 Tip: Scroll horizontally to see all columns including Target Date and Actual Date
              </div>
            )}
          </CardHeader>
          <CardContent>
            {formData.action_items.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No action items added yet. Click "Add Action Item" to begin.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[1200px]"> {/* Ensure table has enough width for all columns */}
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-600">
                        <th className="text-left py-3 px-2 text-gray-300 w-[50px]">No.</th>
                        <th className="text-left py-3 px-2 text-gray-300 w-[200px]">Item</th>
                        <th className="text-left py-3 px-2 text-gray-300 w-[120px]">For/POC</th>
                        <th className="text-center py-3 px-2 text-gray-300 w-[80px]">Briefed POC</th>
                        <th className="text-left py-3 px-2 text-gray-300 w-[120px]">Start Date</th>
                        <th className="text-left py-3 px-2 text-gray-300 w-[150px]">Status</th>
                        <th className="text-left py-3 px-2 text-gray-300 w-[120px]">Target Date</th>
                        <th className="text-left py-3 px-2 text-gray-300 w-[120px]">Actual Date</th>
                        <th className="text-center py-3 px-2 text-gray-300 w-[80px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.action_items.map((item, index) => (
                        <tr key={`ics233-${index}`} className="border-b border-gray-700">
                          <td className="py-3 px-2 text-white font-medium">
                            {item.no}
                          </td>
                          <td className="py-3 px-2">
                            <Textarea
                              value={item.item}
                              onChange={(e) => updateActionItem(index, 'item', e.target.value)}
                              placeholder="Describe the action item..."
                              className="bg-gray-900 border-gray-600 text-white placeholder-gray-400 min-h-[60px] resize-none w-full"
                            />
                          </td>
                          <td className="py-3 px-2">
                            <Input
                              value={item.for_poc}
                              onChange={(e) => updateActionItem(index, 'for_poc', e.target.value)}
                              placeholder="Person/Unit responsible"
                              className="bg-gray-900 border-gray-600 text-white placeholder-gray-400 w-full"
                            />
                          </td>
                          <td className="py-3 px-2 text-center">
                            <Checkbox
                              checked={item.briefed_poc}
                              onCheckedChange={(checked) => updateActionItem(index, 'briefed_poc', checked)}
                              className="data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600"
                            />
                          </td>
                          <td className="py-3 px-2">
                            <Input
                              type="date"
                              value={item.start_date}
                              onChange={(e) => updateActionItem(index, 'start_date', e.target.value)}
                              className="bg-gray-900 border-gray-600 text-white w-full"
                            />
                          </td>
                          <td className="py-3 px-2">
                            <Textarea
                              value={item.status}
                              onChange={(e) => updateActionItem(index, 'status', e.target.value)}
                              placeholder="Current status..."
                              className="bg-gray-900 border-gray-600 text-white placeholder-gray-400 min-h-[60px] resize-none w-full"
                            />
                          </td>
                          <td className="py-3 px-2">
                            <Input
                              type="date"
                              value={item.target_date}
                              onChange={(e) => updateActionItem(index, 'target_date', e.target.value)}
                              className="bg-gray-900 border-gray-600 text-white w-full"
                              title="Target completion date"
                            />
                          </td>
                          <td className="py-3 px-2">
                            <Input
                              type="date"
                              value={item.actual_date}
                              onChange={(e) => updateActionItem(index, 'actual_date', e.target.value)}
                              className="bg-gray-900 border-gray-600 text-white w-full"
                              title="Actual completion date"
                            />
                          </td>
                          <td className="py-3 px-2 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => removeActionItem(index)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingEntry(null);
            }}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || formData.action_items.length === 0}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : (editingEntry ? 'Update' : 'Save')} ICS 233
          </Button>
        </div>
      </div>
    </div>
  );
};

// ICS 309 - Communications Log Form

export default ICS233Form;
